import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface Column<T extends Record<string, any>> {
  key: string;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
  last_page: number;
}

interface DataTableProps<T extends Record<string, any>> {
  columns: Column<T>[];
  data: T[];
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className={`px-6 py-4 ${i < cols - 1 ? 'border-r border-slate-100' : ''}`}>
          <div className="h-4 bg-slate-100 rounded-lg animate-pulse" style={{ width: `${50 + (i % 3) * 20}%` }} />
        </td>
      ))}
    </tr>
  );
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  meta,
  onPageChange,
  isLoading = false,
  emptyMessage = 'No results found.',
  className = '',
}: DataTableProps<T>) {
  const start = meta.total === 0 ? 0 : (meta.page - 1) * meta.per_page + 1;
  const end = Math.min(meta.page * meta.per_page, meta.total);

  const getPageNumbers = (): (number | '...')[] => {
    if (meta.last_page <= 7) {
      return Array.from({ length: meta.last_page }, (_, i) => i + 1);
    }
    const pages: (number | '...')[] = [1];
    if (meta.page > 3) pages.push('...');
    for (let i = Math.max(2, meta.page - 1); i <= Math.min(meta.last_page - 1, meta.page + 1); i++) {
      pages.push(i);
    }
    if (meta.page < meta.last_page - 2) pages.push('...');
    pages.push(meta.last_page);
    return pages;
  };

  return (
    <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col ${className}`}>
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
              {columns.map((col, idx) => (
                <th
                  key={col.key}
                  className={`px-6 py-4 whitespace-nowrap ${idx < columns.length - 1 ? 'border-r border-slate-100' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} cols={columns.length} />
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-16 text-center text-slate-400 text-sm font-medium"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={idx} className="group hover:bg-brand-50/40 transition-colors border-l-2 border-transparent hover:border-brand-400">
                  {columns.map((col, colIdx) => (
                    <td key={col.key} className={`px-6 py-3.5 text-[13px] text-slate-600 whitespace-nowrap group-hover:text-slate-900 transition-colors ${colIdx < columns.length - 1 ? 'border-r border-slate-100' : ''}`}>
                      {col.render
                        ? col.render((row as any)[col.key], row)
                        : String((row as any)[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/50 shrink-0">
        <span className="text-[12px] font-medium text-slate-400">
          {meta.total === 0 ? (
            'No results'
          ) : (
            <>
              Showing{' '}
              <span className="font-bold text-slate-600">{start}–{end}</span>{' '}
              of{' '}
              <span className="font-bold text-slate-600">{meta.total.toLocaleString()}</span>{' '}
              results
            </>
          )}
        </span>

        {meta.last_page > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(meta.page - 1)}
              disabled={meta.page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            {getPageNumbers().map((p, i) =>
              p === '...' ? (
                <span
                  key={`ellipsis-${i}`}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 text-xs select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p as number)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-bold transition-all ${
                    meta.page === p
                      ? 'bg-brand-500 text-slate-900 font-extrabold'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => onPageChange(meta.page + 1)}
              disabled={meta.page === meta.last_page}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
